import { defineStore, storeToRefs } from "pinia"
import { computed, ref, type Ref, watch } from "vue"
import type { User } from "@/models/user.model"

export const useAuthStore = defineStore("authStore", () => {
  const activeUser: Ref<User | null> = ref(null)
  const activeUserChangeHandlers = ref<((user: User | null) => void)[]>([])

  watch(
    () => activeUser.value?.id,
    () => onActiveUserChanged(activeUser.value)
  )

  function onActiveUserChanged(activeUser: User | null) {
    for (const handler of activeUserChangeHandlers.value) {
      handler(activeUser)
    }
  }

  function addActiveUserChangeHandler(handler: (user: User | null) => void) {
    activeUserChangeHandlers.value.push(handler)
  }

  function removeActiveUserChangeHandler(handler: (user: User | null) => void) {
    const index = activeUserChangeHandlers.value.indexOf(handler)
    if (index !== -1) {
      activeUserChangeHandlers.value.splice(index, 1)
    }
  }

  return {
    activeUser,
    addActiveUserChangeHandler,
    removeActiveUserChangeHandler
  }
})

/** The BV's (business units) the active user has a role in. Most tenants have exactly one. */
export function useBusinessUnits() {
  const { activeUser } = storeToRefs(useAuthStore())
  const businessUnits = computed(() => activeUser.value?.businessUnits ?? [])
  return {
    businessUnits,
    hasMultiple: computed(() => businessUnits.value.length > 1),
    byId: (id?: string | null) => businessUnits.value.find((unit) => unit.id === id)
  }
}
